import { useState, useEffect, useRef, useCallback } from "react"
import { EvaluationResult, AgentHistory } from "@/types"
import { Evaluator, EvaluationCase } from "../types"
import { EvaluationResultScope } from "./useEvaluationData"

export interface EvaluationPollingTarget {
  caseId: string
  evaluatorId: string
  agentHistoryId: string
}

interface UseEvaluationPollingReturn {
  runAllLoading: boolean
  setRunAllLoading: (loading: boolean) => void
  startPollingTargets: (targets: EvaluationPollingTarget[]) => void
}

const getTargetKey = (target: EvaluationPollingTarget) =>
  `${target.caseId}:${target.evaluatorId}:${target.agentHistoryId}`

const resultToTarget = (
  result: EvaluationResult
): EvaluationPollingTarget | null => {
  if (!result.agentHistoryId) return null
  return {
    caseId: result.evaluationCaseId,
    evaluatorId: result.evaluatorId,
    agentHistoryId: result.agentHistoryId,
  }
}

export const useEvaluationPolling = (
  cases: EvaluationCase[],
  selectedEvaluator: Evaluator | null,
  selectedAgentHistory: AgentHistory | null,
  results: EvaluationResult[],
  fetchEvaluationResultsForScope: (
    scope: EvaluationResultScope
  ) => Promise<EvaluationResult[]>,
  detailPollingTarget?: EvaluationPollingTarget | null
): UseEvaluationPollingReturn => {
  const [runAllLoading, setRunAllLoading] = useState(false)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const activeTargetsRef = useRef<Map<string, EvaluationPollingTarget>>(
    new Map()
  )
  const [activeTargetKeys, setActiveTargetKeys] = useState<string[]>([])

  const syncActiveTargetKeys = useCallback(() => {
    setActiveTargetKeys(Array.from(activeTargetsRef.current.keys()))
  }, [])

  const startPollingTargets = useCallback(
    (targets: EvaluationPollingTarget[]) => {
      let changed = false
      targets.forEach((target) => {
        const key = getTargetKey(target)
        if (!activeTargetsRef.current.has(key)) {
          activeTargetsRef.current.set(key, target)
          changed = true
        }
      })
      if (changed) syncActiveTargetKeys()
    },
    [syncActiveTargetKeys]
  )

  const removePollingTargets = useCallback(
    (targetKeys: string[]) => {
      let changed = false
      targetKeys.forEach((key) => {
        if (activeTargetsRef.current.delete(key)) {
          changed = true
        }
      })
      if (changed) syncActiveTargetKeys()
    },
    [syncActiveTargetKeys]
  )

  // Polling function uses the active target registry and fetches all targets in one batch.
  const pollResults = useCallback(async () => {
    const targets = Array.from(activeTargetsRef.current.values())
    if (!targets.length) return

    const fetchedResults = await fetchEvaluationResultsForScope({
      targets,
      includeOutput: true,
      includeReasoningSteps: true,
    })

    const activeTargetKeys = new Set(targets.map(getTargetKey))
    const finishedTargetKeys = fetchedResults
      .map((result) => {
        const target = resultToTarget(result)
        if (!target) return null
        const key = getTargetKey(target)
        if (!activeTargetKeys.has(key)) return null
        return result.status === "running" || result.status === "pending"
          ? null
          : key
      })
      .filter((key): key is string => !!key)

    if (finishedTargetKeys.length) {
      removePollingTargets(finishedTargetKeys)
    }
  }, [fetchEvaluationResultsForScope, removePollingTargets])

  const clearPollingInterval = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  // Add visible global/detail running results to the active polling registry.
  useEffect(() => {
    const caseIds = new Set(cases.map((c) => c.id))
    const runningTargets: EvaluationPollingTarget[] = []

    const relevantResults = results.filter(
      (r) =>
        caseIds.has(r.evaluationCaseId) &&
        r.evaluatorId === selectedEvaluator?.id &&
        r.agentHistoryId === selectedAgentHistory?.id
    )
    relevantResults.forEach((result) => {
      if (result.status !== "running" && result.status !== "pending") return
      const target = resultToTarget(result)
      if (target) runningTargets.push(target)
    })

    if (detailPollingTarget) {
      const detailResult = results.find((result) => {
        const target = resultToTarget(result)
        return target
          ? getTargetKey(detailPollingTarget) === getTargetKey(target)
          : false
      })
      if (
        detailResult?.status === "running" ||
        detailResult?.status === "pending"
      ) {
        runningTargets.push(detailPollingTarget)
      }
    }

    if (runningTargets.length) startPollingTargets(runningTargets)
  }, [
    cases,
    selectedEvaluator,
    selectedAgentHistory,
    detailPollingTarget,
    results,
    startPollingTargets,
  ])

  // Effect: Manage polling interval, always use latest values
  useEffect(() => {
    clearPollingInterval()
    if (!activeTargetKeys.length) {
      setRunAllLoading(false)
      return
    }
    setRunAllLoading(true)
    pollResults() // initial call
    pollingRef.current = setInterval(pollResults, 5000)
    return () => {
      clearPollingInterval()
    }
  }, [activeTargetKeys, pollResults, clearPollingInterval])

  return {
    runAllLoading,
    setRunAllLoading,
    startPollingTargets,
  }
}
